import React, { useState, useMemo } from "react";
import {
    Card,
    Form,
    Input,
    Button,
    message,
    Breadcrumb,
    Divider,
    Row,
    Col,
    Spin,
    Space,
    Select,
    Switch,
    InputNumber,
    Typography,
    Table, // Import Table
    Tooltip,
} from "antd";
import {
    SaveOutlined,
    ArrowLeftOutlined,
    HomeOutlined,
    DollarOutlined,
    PlusOutlined,
    MinusCircleOutlined,
    TagOutlined,
    FileSyncOutlined,
    QuestionCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { templateAPIWithErrorHandling } from "../../../api/feeTemplate/feeTemplate";

const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

// Định nghĩa các hằng số (constants)
const TARGET_TYPES = [
    { value: 'other', label: 'Khác' },
    // { value: 'education', label: 'Giáo dục' },
    // { value: 'residential', label: 'Khu dân cư/Chung cư' },
    // { value: 'office', label: 'Văn phòng/Thương mại' },
];

const ITEM_TYPES = [
    { value: 'fixed', label: 'Cố định' },
    // { value: 'variable', label: 'Biến đổi' },
    // { value: 'optional', label: 'Tùy chọn' },
];

const CALC_METHODS = [
    { value: 'fixed', label: 'Số tiền cố định' },
    // { value: 'by_area', label: 'Theo diện tích (m²)' },
    // { value: 'by_meter', label: 'Theo chỉ số công tơ/đơn vị đo' },
    // { value: 'by_rate', label: 'Theo tỷ lệ % giá trị' },
    // { value: 'by_days', label: 'Theo số ngày' },
];

const initialItem = {
    item_name: 'Khoản mục phí',
    type: 'fixed',
    calc_method: 'fixed',
    unit_price: 0,
    unit_name: '',
    tax: 0,
    is_mandatory: true,
};

export default function StoreFeeTemplate() {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    // Custom validate: unit_name required if calc_method is not 'fixed'
    const validateUnitName = (getFieldValue, index) => ({
        validator(_, value) {
            // Lấy calc_method của hàng hiện tại
            const calcMethod = getFieldValue(['items', index, 'calc_method']);

            if (calcMethod && calcMethod !== 'fixed' && !value) {
                return Promise.reject(new Error('Bắt buộc cho phương thức này!'));
            }
            return Promise.resolve();
        },
    });

    // Định nghĩa các cột của bảng
    const columns = useMemo(() => (fields, remove) => [
        {
            title: '#',
            dataIndex: 'index',
            key: 'index',
            width: '2%',
            render: (text, record, index) => index + 1,
        },
        {
            title: 'Tên khoản mục (*)',
            dataIndex: 'item_name',
            key: 'item_name',
            width: '20%',
            render: (_, field) => (
                <Form.Item
                    {...field}
                    name={[field.name, 'item_name']}
                    fieldKey={[field.fieldKey, 'item_name']}
                    rules={[{ required: true, message: 'Nhập tên khoản mục!' }]}
                    className="mb-0"
                    style={{ marginBottom:"0px" }}
                >
                    <Input placeholder="Tên khoản mục" />
                </Form.Item>
            ),
        },
        {
            title: 'Phân loại',
            dataIndex: 'type',
            key: 'type',
            width: '10%',
            render: (_, field) => (
                <Form.Item
                    {...field}
                    name={[field.name, 'type']}
                    fieldKey={[field.fieldKey, 'type']}
                    rules={[{ required: true, message: 'Chọn loại!' }]}
                    className="mb-0"
                    style={{ marginBottom:"0px" }}
                >
                    <Select placeholder="Loại">
                        {ITEM_TYPES.map(type => (
                            <Option key={type.value} value={type.value}>{type.label}</Option>
                        ))}
                    </Select>
                </Form.Item>
            ),
        },
        {
            title: 'Phương thức tính (*)',
            dataIndex: 'calc_method',
            key: 'calc_method',
            width: '15%',
            render: (_, field) => (
                <Form.Item
                    {...field}
                    name={[field.name, 'calc_method']}
                    fieldKey={[field.fieldKey, 'calc_method']}
                    rules={[{ required: true, message: 'Chọn phương thức!' }]}
                    className="mb-0"
                    style={{ marginBottom:"0px" }}
                >
                    <Select placeholder="Phương thức">
                        {CALC_METHODS.map(method => (
                            <Option key={method.value} value={method.value}>{method.label}</Option>
                        ))}
                    </Select>
                </Form.Item>
            ),
        },
        {
            title: 'Đơn giá/Số tiền (*)',
            dataIndex: 'unit_price',
            key: 'unit_price',
            width: '15%',
            render: (_, field) => (
                <Form.Item
                    {...field}
                    name={[field.name, 'unit_price']}
                    fieldKey={[field.fieldKey, 'unit_price']}
                    rules={[{ required: true, message: 'Nhập giá!' }]}
                    className="mb-0"
                    style={{ marginBottom:"0px" }}
                >
                    <InputNumber
                        min={0}
                        style={{ width: '100%' }}
                        formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={value => value.replace(/\$\s?|(,*)/g, '')}
                        placeholder="0"
                    />
                </Form.Item>
            ),
        },
        {
          title: 'Thuế',
          dataIndex: 'tax',
          key: 'tax',
          width: '10%',
          render: (_, field) => (
            <Form.Item
              {...field}
              name={[field.name, 'tax']}
              fieldKey={[field.fieldKey, 'tax']}
              className="mb-0"
              style={{ marginBottom:"0px" }}
            >
                <InputNumber
                    min={0}
                    max={100}
                    formatter={(value) => `${value}`}
                    placeholder="0"
                    style={{ width: "100%" }}
                    addonAfter="%"
                />
            </Form.Item>
          ),
        },
        {
            title: (
                <>
                    Đơn vị&nbsp;
                    <Tooltip title="Bắt buộc nếu Phương thức tính không phải là 'Số tiền cố định'">
                        <QuestionCircleOutlined />
                    </Tooltip>
                </>
            ),
            dataIndex: 'unit_name',
            key: 'unit_name',
            width: '11%',
            render: (_, field) => (
                <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, curValues) =>
                        prevValues.items[field.name]?.calc_method !== curValues.items[field.name]?.calc_method
                    }
                >
                    {({ getFieldValue }) => (
                        <Form.Item
                            {...field}
                            name={[field.name, 'unit_name']}
                            fieldKey={[field.fieldKey, 'unit_name']}
                            rules={[
                                { max: 50, message: "Không quá 50 ký tự" },
                                validateUnitName(getFieldValue, field.name), // Custom rule
                            ]}
                            className="mb-0"
                    style={{ marginBottom:"0px" }}
                        >
                            <Input placeholder="m2, kWh, %" />
                        </Form.Item>
                    )}
                </Form.Item>
            ),
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: '7%',
            render: (_, field) => (
                <div style={{ textAlign: 'center' }}>
                    {fields.length > 1 ? (
                        <Button
                            type="text"
                            icon={<MinusCircleOutlined className="text-red-500 text-lg" />}
                            onClick={() => remove(field.name)}
                            title="Xóa khoản mục"
                        />
                    ) : null}
                </div>
            ),
        },
    ], []);

    // Handle form submission
    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            // API call to create template
            const response = await templateAPIWithErrorHandling.createTemplate(values);

            if (response.data && response.data.success) {
                message.success("Thêm mẫu khoản thu thành công!");
            } else {
                throw new Error(response.data?.message || "Lỗi không xác định.");
            }

            form.resetFields();
            navigate("/templates");
        } catch (error) {
            if (error.response && error.response.status === 422 && error.response.data.errors) {
                const backendErrors = error.response.data.errors;

                // Xử lý lỗi validation phức tạp (ví dụ: trùng tên từ Service)
                if (backendErrors.name) {
                    form.setFields([{ name: 'name', errors: backendErrors.name }]);
                }

                message.error("Vui lòng kiểm tra lại dữ liệu nhập.");
            } else {
                message.error(error.message || "Không thể thêm mẫu khoản thu");
                console.error("Error creating template:", error);
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Các hàm khác không thay đổi
    const handleReset = () => { form.resetFields(); message.info("Đã xóa tất cả dữ liệu nhập"); };
    const handleBack = () => { navigate("/templates"); message.info("Quay lại danh sách mẫu khoản thu"); };

    return (
        <div className="bg-gray-50 min-h-screen p-6">
            {/* <Card className="shadow-lg max-w-7xl mx-auto"> */}
            <Card className="shadow-lg mx-auto">
                {/* Breadcrumb */}
                <div className="mb-6">
                    <Breadcrumb
                        separator=">"
                        className="mb-4"
                        items={[
                            { href: "/", title: (<><HomeOutlined /> Trang chủ</>) },
                            { href: "/templates", title: (<><DollarOutlined /> Mẫu khoản thu</>) },
                            { title: (<><PlusOutlined /> Thêm mới</>) },
                        ]}
                    />
                </div>

                <Divider />

                {/* Form */}
                <Spin spinning={submitting} tip="Đang lưu thông tin...">
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        requiredMark="optional"
                        className="space-y-4"
                        initialValues={{
                            status: true,
                            target_type: 'other',
                            items: [initialItem]
                        }}
                    >
                        <Row gutter={24}>
                            {/* CỘT THÔNG TIN CHUNG */}
                            <Col xs={24} lg={24}>
                                <Card
                                    title={
                                        <span className="text-blue-600">
                                            <TagOutlined className="mr-2" />
                                            Thông tin chung
                                        </span>
                                    }
                                    className="h-full shadow-sm border-blue-200"
                                >
                                    <Form.Item
                                        name="name"
                                        label={<span className="font-medium">Tên mẫu khoản thu (*)</span>}
                                        rules={[
                                            { required: true, message: "Vui lòng nhập tên mẫu khoản thu!" },
                                            { max: 255, message: "Tên mẫu không được vượt quá 255 ký tự!" },
                                        ]}
                                    >
                                        <Input
                                            placeholder="Nhập tên mẫu khoản thu"
                                            size="default"
                                            className="rounded-lg"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="description"
                                        label={<span className="font-medium">Mô tả</span>}
                                    >
                                        <TextArea
                                            placeholder="Mô tả chi tiết về mẫu khoản thu (max 500 ký tự)..."
                                            rows={4}
                                            className="rounded-lg"
                                            maxLength={500}
                                            showCount
                                        />
                                    </Form.Item>


                                    <Row gutter={[24, 16]}>
                                        <Col xs={24} md={16}>
                                            <Form.Item
                                                name="target_type"
                                                label={<span className="font-medium">Loại đối tượng áp dụng (*)</span>}
                                                rules={[{ required: true, message: "Vui lòng chọn loại đối tượng!" }]}
                                            >
                                                <Select placeholder="Chọn loại đối tượng" size="default" className="rounded-lg">
                                                    {TARGET_TYPES.map(type => (
                                                        <Option key={type.value} value={type.value}>
                                                            {type.label}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        
                                        </Col>
                                        <Col xs={24} md={8}>
                                            <Form.Item
                                                name="status"
                                                label={<span className="font-medium">Trạng thái</span>}
                                                valuePropName="checked"
                                            >
                                                <Switch
                                                    checkedChildren="Kích hoạt"
                                                    unCheckedChildren="Vô hiệu hóa"
                                                    className="bg-gray-300"
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                </Card>
                            </Col>

                            {/* CỘT DANH SÁCH KHOẢN MỤC (TABLE) */}
                            <Col xs={24} lg={24} className="mt-6">
                                <Card
                                    title={
                                        <span className="text-blue-600">
                                            <FileSyncOutlined className="mr-2" />
                                            Danh sách khoản mục phí (*)
                                        </span>
                                    }
                                    className="h-full shadow-sm border-blue-200"
                                >
                                    <Form.List
                                        name="items"
                                        rules={[
                                            {
                                                validator: async (_, items) => {
                                                    if (!items || items.length < 1) {
                                                        return Promise.reject(new Error('Mẫu khoản thu phải có ít nhất 1 khoản mục.'));
                                                    }
                                                },
                                            },
                                        ]}
                                    >
                                        {(fields, { add, remove }, { errors }) => (
                                            <>
                                                <Table
                                                    dataSource={fields}
                                                    columns={columns(fields, remove)}
                                                    rowKey="key"
                                                    pagination={false}
                                                    size="small"
                                                    bordered
                                                    scroll={{ x: 'max-content' }}
                                                    className="editable-table"
                                                />

                                                {/* Hiển thị lỗi tổng hợp của Form.List */}
                                                <Form.Item>{errors.length > 0 ? (
                                                    <div className="text-red-500 p-2 border border-red-200 bg-red-50 rounded">
                                                        {errors.map((err, i) => <p key={i} className="mb-0">{err}</p>)}
                                                    </div>
                                                ) : null}</Form.Item>

                                                <Button
                                                    type="dashed"
                                                    onClick={() => add(initialItem)}
                                                    block
                                                    icon={<PlusOutlined />}
                                                    className="h-10 border-blue-400 text-blue-600 hover:border-blue-600 mt-2"
                                                >
                                                    Thêm Khoản mục phí
                                                </Button>
                                            </>
                                        )}
                                    </Form.List>
                                </Card>
                            </Col>
                        </Row>

                        {/* Action Buttons */}
                        <Card className="mt-6 shadow-sm bg-gradient-to-r from-blue-50 to-green-50">
                            <Row
                                justify="space-between"
                                align="middle"
                            >
                                <Col>
                                    <Button
                                        icon={<ArrowLeftOutlined />}
                                        onClick={handleBack}
                                        size="default"
                                        className="shadow-sm"
                                    >
                                        Quay lại
                                    </Button>
                                </Col>
                                <Col>
                                    <Space size="small">
                                        <Button
                                            type="default"
                                            size="default"
                                            onClick={handleReset}
                                            className="min-w-32 rounded-lg shadow-sm"
                                        >
                                            Làm mới
                                        </Button>
                                        <Button
                                            type="primary"
                                            size="default"
                                            htmlType="submit"
                                            loading={submitting}
                                            icon={<SaveOutlined />}
                                            className="min-w-40 rounded-lg shadow-sm bg-gradient-to-r from-blue-500 to-blue-600 border-none"
                                        >
                                            {submitting ? "Đang lưu..." : "Lưu thông tin"}
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Card>
                    </Form>
                </Spin>
            </Card>
        </div>
    );
}